import { useCallback, useEffect, useState } from 'react';
import {
  BarChart3,
  Bot,
  ClipboardList,
  DollarSign,
  LayoutGrid,
  Users,
  Activity,
  Apple,
} from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import SpiritualHomeButton from '../spiritual/user/SpiritualHomeButton';
import '../../styles/ecosystem-fit-ux.css';

const QUICK_LINKS = [
  { id: 'progress-training', labelKey: 'nav.tracking_training', fallback: 'Seguimiento entreno', Icon: Activity },
  { id: 'progress-food', labelKey: 'nav.tracking_food', fallback: 'Seguimiento food', Icon: Apple, foodOnly: true },
  { id: 'adminusers', labelKey: 'nav.myusers', fallback: 'Clientes', Icon: Users },
  { id: 'coachai', labelKey: 'nav.coach_ai', fallback: 'IA Rutinas', Icon: Bot },
  { id: 'admintraining', labelKey: 'nav.edit_routines', fallback: 'Editar rutinas', Icon: ClipboardList },
  { id: 'admingallery', labelKey: 'nav.gallery', fallback: 'Biblioteca', Icon: LayoutGrid },
  { id: 'training-stats', labelKey: 'nav.training_stats', fallback: 'Estadísticas', Icon: BarChart3 },
  { id: 'finance-training', labelKey: 'nav.finance_coach', fallback: 'Finanzas afiliados', Icon: DollarSign },
];

const HYBRID_GYM_LINKS = [
  { id: 'liveclasses', labelKey: 'nav.liveclasses', fallback: 'Clases en Vivo', Icon: Activity },
  { id: 'd28d-gallery', labelKey: 'nav.gallery_classes', fallback: 'GRABACIONES DE CLASE', Icon: LayoutGrid },
  { id: 'admingyms', labelKey: 'nav.mybrand', fallback: 'Mi marca', Icon: Users },
];

function attentionScore(client) {
  let score = 0;
  if (!client.has_plan) score += 3;
  if ((client.unread_notifications || 0) > 0) score += 2;
  const days = client.last_session?.fecha
    ? Math.floor((Date.now() - new Date(client.last_session.fecha).getTime()) / 86400000)
    : 999;
  if (days >= 7) score += 3;
  if ((client.adherence || 0) < 50) score += 2;
  return score;
}

/** Home operativo del coach: resumen ejecutivo + accesos rápidos (sin duplicar el hub analítico). */
export default function CoachHomeCenter({
  onNavigate,
  onOpenTrainingPanel,
  onOpenSpiritual,
  showSpiritual = false,
  variant = 'coach',
  showFoodTracking = false,
}) {
  const { t } = useI18n();
  const [summary, setSummary] = useState({
    loading: true,
    clients: 0,
    alerts: 0,
    attention: 0,
    recent: [],
  });

  const loadSummary = useCallback(async () => {
    try {
      const clientsRes = await api.get('/training/coach/clients');
      const list = clientsRes.data?.data || [];
      let notifs = [];
      try {
        const notifRes = await api.get('/training/coach/notifications', { params: { unread: 'true' } });
        notifs = notifRes.data?.data || [];
      } catch {
        notifs = [];
      }
      const needing = list.filter((c) => attentionScore(c) >= 3);
      const recent = [...list]
        .sort((a, b) => {
          const da = a.last_session?.fecha ? new Date(a.last_session.fecha).getTime() : 0;
          const db = b.last_session?.fecha ? new Date(b.last_session.fecha).getTime() : 0;
          return db - da;
        })
        .slice(0, 4);
      setSummary({
        loading: false,
        clients: list.length,
        alerts: notifs.length,
        attention: needing.length,
        recent,
      });
    } catch {
      setSummary((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      await loadSummary();
      if (!active) return;
    })();
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadSummary();
    };
    document.addEventListener('visibilitychange', onVisible);
    const onStorage = (e) => {
      if (e.key === 'coach.clients.refresh') loadSummary();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      active = false;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('storage', onStorage);
    };
  }, [loadSummary]);

  useEffect(() => {
    try {
      const flag = sessionStorage.getItem('coach.clients.refresh');
      if (flag) {
        sessionStorage.removeItem('coach.clients.refresh');
        loadSummary();
      }
    } catch { /* noop */ }
  }, [loadSummary]);

  const openClient = (clientId) => {
    onNavigate?.('progress-training');
    try {
      sessionStorage.setItem('coach.tracking.selectedUserId', String(clientId));
    } catch {
      /* ignore */
    }
  };

  const quickLinks = QUICK_LINKS.filter((link) => !link.foodOnly || showFoodTracking);

  return (
    <div className="dashboard-main-view space-y-6">
      <header>
        <h1 className="d28d-page-title text-2xl">{t('nav.home', 'Inicio')}</h1>
        <p className="d28d-text-muted text-sm mt-1">
          {t('coach.home.subtitle_v3', 'Resumen operativo y accesos directos. El detalle analítico está en Seguimiento y Estadísticas.')}
        </p>
      </header>

      <section className="ef-stat-grid">
        <SummaryCard
          label={t('coach.home.clients', 'Clientes activos')}
          value={summary.loading ? '…' : summary.clients}
        />
        <SummaryCard
          label={t('coach.home.alerts', 'Alertas pendientes')}
          value={summary.loading ? '…' : summary.alerts}
          highlight={summary.alerts > 0}
        />
        <SummaryCard
          label={t('coach.home.attention', 'Requieren atención')}
          value={summary.loading ? '…' : summary.attention}
          highlight={summary.attention > 0}
          onClick={() => onNavigate?.('progress-training')}
        />
        <button
          type="button"
          className="ef-stat-card text-left hover:border-[var(--d28d-yellow)] transition"
          onClick={() => onOpenTrainingPanel?.()}
        >
          <span className="ef-stat-card__label">{t('coach.panel.title', 'Panel del Entrenador')}</span>
          <span className="ef-stat-card__value text-sm">{t('coach.panel.open', 'Mis asesorados →')}</span>
        </button>
      </section>

      {!summary.loading && summary.alerts > 0 && (
        <section className="card p-4 border border-amber-200/40 bg-amber-500/10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-[var(--d28d-text)]">
              {t('coach.home.alert_cta', 'Tienes alertas sin revisar. Abre Seguimiento para ver el detalle.')}
            </p>
            <button type="button" className="btn-primary text-sm" onClick={() => onNavigate?.('progress-training')}>
              {t('nav.tracking_training', 'Seguimiento entreno')}
            </button>
          </div>
        </section>
      )}

      {showSpiritual ? (
        <SpiritualHomeButton onOpen={onOpenSpiritual} />
      ) : null}

      {!summary.loading && summary.recent.length > 0 && (
        <section className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-bold text-[var(--d28d-text)] text-sm uppercase tracking-wide">
              {t('coach.home.recent_activity', 'Actividad reciente')}
            </h2>
            <button type="button" className="text-xs text-[var(--d28d-yellow)] font-semibold" onClick={() => onNavigate?.('progress-training')}>
              {t('coach.home.view_all', 'Ver todos')}
            </button>
          </div>
          <ul className="card divide-y divide-[var(--d28d-border)]">
            {summary.recent.map((c) => (
              <li key={c.id}>
                <button type="button" className="ef-client-row w-full text-left" onClick={() => openClient(c.id)}>
                  <span className="font-medium text-[var(--d28d-text)]">{c.nombre}</span>
                  <span className="text-xs d28d-text-muted shrink-0 text-right">
                    {c.adherence ?? 0}% · {c.last_session?.fecha || t('coach.home.no_sessions', 'Sin sesiones')}
                    {attentionScore(c) >= 3 ? <span className="ef-badge-alert ml-2">{t('coach.home.needs_attention', 'Atención')}</span> : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-3">
        <h2 className="font-bold text-[var(--d28d-text)] text-sm uppercase tracking-wide">
          {t('coach.home.quick_access', 'Accesos rápidos')}
        </h2>
        <div className="ef-quick-grid ef-quick-grid--coach">
          {[...quickLinks, ...(variant === 'hybrid-gym' ? HYBRID_GYM_LINKS : [])].map((link) => (
            <button
              key={link.id}
              type="button"
              className="ef-quick-link"
              onClick={() => onNavigate?.(link.id)}
            >
              <link.Icon className="w-5 h-5 ef-quick-link__icon" aria-hidden />
              <span className="ef-quick-link__label">{t(link.labelKey, link.fallback)}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function SummaryCard({ label, value, highlight = false, onClick = null }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      className={`ef-stat-card text-left ${highlight ? 'border-amber-400/50 bg-amber-500/10' : ''} ${onClick ? 'hover:border-[var(--d28d-yellow)] transition cursor-pointer' : ''}`}
      onClick={onClick || undefined}
    >
      <span className="ef-stat-card__label">{label}</span>
      <p className="ef-stat-card__value ef-stat-card__value--metric">{value}</p>
    </Tag>
  );
}
